import { useState } from 'react'
import { X, Plus, X as RemoveIcon } from 'lucide-react'
import { useCreateClient, useUpdateClient, useClientTags } from '@hooks/useClients'
import { Client } from '@/types'

interface ClientFormProps {
  client?: Client | null
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  email: string
  fullName: string
  company: string
  status: Client['status']
  tags: string[]
  metadata: Record<string, any>
}

export function ClientForm({ client, onClose, onSuccess }: ClientFormProps) {
  const [formData, setFormData] = useState<FormData>({
    email: client?.email || '',
    fullName: client?.fullName || '',
    company: client?.company || '',
    status: client?.status || 'NEW',
    tags: client?.tags || [],
    metadata: client?.metadata || {},
  })
  const [newTag, setNewTag] = useState('')
  const [newMetadataKey, setNewMetadataKey] = useState('')
  const [newMetadataValue, setNewMetadataValue] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createMutation = useCreateClient()
  const updateMutation = useUpdateClient(client?.id || 0)
  const { data: availableTags } = useClientTags()

  const isEditMode = !!client

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    const newErrors: Record<string, string> = {}

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})

    try {
      // Build customData including tags if present
      const customData: Record<string, any> = { ...formData.metadata }
      if (formData.tags.length > 0) {
        customData.tags = formData.tags
      }

      const payload = {
        email: formData.email.trim(),
        fullName: formData.fullName.trim() || undefined,
        company: formData.company.trim() || undefined,
        status: formData.status,
        customData: Object.keys(customData).length > 0 ? customData : undefined,
      }

      if (isEditMode) {
        await updateMutation.mutateAsync(payload)
      } else {
        await createMutation.mutateAsync(payload)
      }

      onSuccess()
      onClose()
    } catch (error: any) {
      setErrors({ submit: error.response?.data?.message || 'Failed to save client' })
    }
  }

  const handleAddTag = (tag: string) => {
    const trimmedTag = tag.trim()
    if (trimmedTag && !formData.tags.includes(trimmedTag)) {
      setFormData({ ...formData, tags: [...formData.tags, trimmedTag] })
      setNewTag('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter((t) => t !== tag) })
  }

  const handleAddMetadata = () => {
    const key = newMetadataKey.trim()
    const value = newMetadataValue.trim()

    if (key && value) {
      setFormData({
        ...formData,
        metadata: { ...formData.metadata, [key]: value },
      })
      setNewMetadataKey('')
      setNewMetadataValue('')
    }
  }

  const handleRemoveMetadata = (key: string) => {
    const newMetadata = { ...formData.metadata }
    delete newMetadata[key]
    setFormData({ ...formData, metadata: newMetadata })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-2 hover:bg-gray-100"
        >
          <X size={20} />
        </button>

        <h2 className="mb-6 text-xl font-semibold text-gray-900">
          {isEditMode ? 'Edit Client' : 'Add New Client'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`mt-1 w-full rounded-lg border ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              } px-3 py-2 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`}
              placeholder="client@example.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <input
              type="text"
              id="fullName"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="John Doe"
            />
          </div>

          {/* Company */}
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700">
              Company
            </label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Acme Corp"
            />
            <p className="mt-1 text-xs text-gray-500">
              Used for email personalization with {'{{company}}'} variable
            </p>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as Client['status'] })
              }
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="PENDING">Pending</option>
              <option value="EMAIL_1_SENT">Email 1 Sent</option>
              <option value="EMAIL_2_SENT">Email 2 Sent</option>
              <option value="EMAIL_3_SENT">Email 3 Sent</option>
              <option value="RESPONDED">Responded</option>
              <option value="UNSUBSCRIBED">Unsubscribed</option>
              <option value="BOUNCED">Bounced</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Tags</label>

            {/* Available Tags */}
            {availableTags && availableTags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {availableTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() =>
                      formData.tags.includes(tag)
                        ? handleRemoveTag(tag)
                        : handleAddTag(tag)
                    }
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      formData.tags.includes(tag)
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}

            {/* Selected Tags */}
            {formData.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-blue-900"
                    >
                      <RemoveIcon size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add Custom Tag */}
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTag(newTag)
                  }
                }}
                placeholder="Add custom tag..."
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <button
                type="button"
                onClick={() => handleAddTag(newTag)}
                className="flex items-center gap-1 rounded-lg bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          </div>

          {/* Custom Metadata */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Custom Fields (Metadata)
            </label>

            {/* Existing Metadata */}
            {Object.keys(formData.metadata).length > 0 && (
              <div className="mt-2 space-y-2">
                {Object.entries(formData.metadata).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2"
                  >
                    <span className="flex-1 text-sm">
                      <span className="font-medium text-gray-700">{key}:</span>{' '}
                      <span className="text-gray-600">{String(value)}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveMetadata(key)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <RemoveIcon size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Metadata */}
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={newMetadataKey}
                onChange={(e) => setNewMetadataKey(e.target.value)}
                placeholder="Field name"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <input
                type="text"
                value={newMetadataValue}
                onChange={(e) => setNewMetadataValue(e.target.value)}
                placeholder="Value"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <button
                type="button"
                onClick={handleAddMetadata}
                className="flex items-center gap-1 rounded-lg bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                <Plus size={16} />
                Add
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Add custom fields to store additional information about this client
            </p>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="rounded-lg bg-primary px-4 py-2 font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : isEditMode
                ? 'Update Client'
                : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
